import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { resources } from "@constants/hardcoded-strings";
import { POLICY_COMPONENT_DETAILS_REFERENCE_DATA_TYPES, PolicyComponentDetailsReferenceData } from "@constants/reference-data";
import { CardContainer } from "@containers/common/card-container/card-container";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { LoadingIndicator } from "@containers/common/loading-indicator/loading-indicator";
import { CommonReferenceDataPageTypes } from "@enums/common-reference-data-page-types";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { ReferenceDataSourceTypes } from "@enums/reference-data-source-types";
import { useReferenceDataSelector } from "@hooks/use-reference-data-selector";
import { FormattedPolicyComponent, PolicyComponent } from "@idw-types/data/reference-data/generic/policy-component";
import { updatePolicyComponentReduxRequest } from "@redux-requests/policy-component/update-policy-component";
import { resetTableSettingsTableState } from "@redux/actions/table-settings-actions";
import { getPolicyComponentDetailsAssociatedPoliciesReduxRequest } from "@redux-requests/policy-component-details/get-policy-component-details-associated-policies";
import { getPolicyComponentDetailsReduxRequest } from "@redux-requests/policy-component-details/get-policy-component-details";
import { getCommonReferenceDataReduxRequest } from "@redux-requests/reference-data/get-common-reference-data-redux-request";
import { PolicyComponentDetailsComponent } from "@containers/policy-component-details/policy-component-details-component/policy-component-details-component";
import { getTableSettingsState } from "@redux/selectors/root-selectors";
import { getPolicyComponentDetailsDataState } from "@redux/selectors/policy-component-details-selectors";

export const PolicyComponentDetailsContainer = () => {
    const dispatch = useDispatch();

    const { policyComponentId } = useParams<{ policyComponentId: string }>();
    const [shouldLoadPolicyComponentDetails, setShouldLoadPolicyComponentDetails] = useState(true);
    const [shouldLoadDetailsReferenceData, setShouldLoadDetailsReferenceData] = useState(true);
    const [shouldLoadAssociatedPolicies, setShouldLoadAssociatedPolicies] = useState(true);
    const [shouldReloadDetailsReferenceData, setShouldReloadDetailsReferenceData] = useState(false);

    const { policyComponentDetailsAssociatedPolicyComponents: associatedPoliciesTableState } = useSelector(getTableSettingsState);
    const {
        isLoadingPolicyComponentDetails,
        policyComponentDetails,
        isLoadingAssociatedPolicies,
        associatedPolicies,
    } = useSelector(getPolicyComponentDetailsDataState);
    const referenceData = useReferenceDataSelector<PolicyComponentDetailsReferenceData>(POLICY_COMPONENT_DETAILS_REFERENCE_DATA_TYPES);

    useEffect(() => {
        dispatch(resetTableSettingsTableState(TableSettingsTableTypes.POLICY_COMPONENT_DETAILS_ASSOCIATED_POLICY_COMPONENTS));
    }, []);

    useEffect(() => {
        if (!referenceData.isLoading.policyComponentDetails && shouldLoadDetailsReferenceData) {
            setShouldLoadDetailsReferenceData(false);
            getCommonReferenceDataReduxRequest(dispatch)(
                POLICY_COMPONENT_DETAILS_REFERENCE_DATA_TYPES,
                ReferenceDataSourceTypes.SERVICE_CONFIGURATION,
                CommonReferenceDataPageTypes.POLICY_COMPONENT_DETAILS
            );
        }
    }, []);


    useEffect(() => {
        if (shouldReloadDetailsReferenceData) {
            setShouldLoadDetailsReferenceData(false);
            getCommonReferenceDataReduxRequest(dispatch)(
                POLICY_COMPONENT_DETAILS_REFERENCE_DATA_TYPES,
                ReferenceDataSourceTypes.SERVICE_CONFIGURATION,
                CommonReferenceDataPageTypes.POLICY_COMPONENT_DETAILS
            );
            setShouldReloadDetailsReferenceData(false);
        }
    }, [shouldReloadDetailsReferenceData]);


    const isLoading = useMemo(() => {
        return (
            isLoadingPolicyComponentDetails ||
            referenceData.isLoading.policyComponentDetails ||
            shouldLoadPolicyComponentDetails ||
            shouldLoadAssociatedPolicies ||
            shouldLoadDetailsReferenceData ||
            isLoadingAssociatedPolicies
        );
    }, [
        isLoadingPolicyComponentDetails,
        referenceData.isLoading.policyComponentDetails,
        shouldLoadPolicyComponentDetails,
        shouldLoadAssociatedPolicies,
        shouldLoadDetailsReferenceData,
        isLoadingAssociatedPolicies
    ]);

    const isReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.axnCompanies &&
            referenceData.axnAcquiredAttributes &&
            referenceData.axnAssertions &&
            referenceData.deployedPolicyComponents &&
            referenceData.axnAttributeTypeFields &&
            referenceData.axnServiceOfferings &&
            referenceData.apAssertions &&
            referenceData.simpleAXNRPServices &&
            referenceData.simpleAXNRPServices &&
            referenceData.axnAttributeTypes &&
            referenceData.axnAvailableAssertionStatuses
        );
    }, [
        referenceData.axnCompanies,
        referenceData.axnAcquiredAttributes,
        referenceData.axnAssertions,
        referenceData.deployedPolicyComponents,
        referenceData.axnAttributeTypeFields,
        referenceData.axnServiceOfferings,
        referenceData.apAssertions,
        referenceData.simpleAXNRPServices,
        referenceData.axnAttributeTypes,
        referenceData.axnAvailableAssertionStatuses
    ]);

    useEffect(() => {
        if (!isLoadingPolicyComponentDetails && shouldLoadPolicyComponentDetails && isReferenceDataPresent) {
            setShouldLoadPolicyComponentDetails(false);
            getPolicyComponentDetailsReduxRequest(dispatch)(policyComponentId, referenceData.axnAttributeTypeFields);
        }
    }, [isReferenceDataPresent, shouldLoadPolicyComponentDetails, isLoadingPolicyComponentDetails]);

    useEffect(() => {
        if (!isLoadingAssociatedPolicies && shouldLoadAssociatedPolicies && isReferenceDataPresent) {
            setShouldLoadAssociatedPolicies(false);
            getPolicyComponentDetailsAssociatedPoliciesReduxRequest(dispatch)(
                policyComponentId,
                referenceData.simpleAXNWorkflows,
                referenceData.simpleAXNRPServiceWorkflows,
                referenceData.axnRelyingParties,
                referenceData.axnCompanies,
                referenceData.simpleAXNRPServices
            );
        }
    }, [isReferenceDataPresent, shouldLoadAssociatedPolicies, isLoadingAssociatedPolicies]);

    const isLoaded = useMemo(() => {
        return !!(
            !isLoading &&
            isReferenceDataPresent &&
            policyComponentDetails &&
            !shouldLoadPolicyComponentDetails &&
            !shouldLoadDetailsReferenceData &&
            !shouldLoadAssociatedPolicies &&
            !isLoadingAssociatedPolicies &&
            associatedPolicies
        );
    }, [
        isLoading,
        isReferenceDataPresent,
        policyComponentDetails,
        shouldLoadPolicyComponentDetails,
        shouldLoadDetailsReferenceData,
        shouldLoadAssociatedPolicies,
        isLoadingAssociatedPolicies,
        associatedPolicies
    ]);

    const isDisabled = useMemo(() => {
        if (isLoaded && policyComponentDetails.permissions?.canModify) {
            return false;
        }
        return true;
    }, [isLoaded, policyComponentDetails]);

    const handleSave = async (policyComponentDetails: FormattedPolicyComponent): Promise<PolicyComponent> => {
        const response = await updatePolicyComponentReduxRequest(dispatch)(policyComponentDetails, referenceData.axnAttributeTypeFields);
        if (response) {
            setShouldReloadDetailsReferenceData(true);
        }
        return response;
    };

    const allowedPolicyComponents = useMemo(() => {
        if (referenceData.deployedPolicyComponents) {
            return referenceData.deployedPolicyComponents.filter(dpc => dpc.id !== policyComponentId);
        }

        return [];
    }, [referenceData.deployedPolicyComponents]);

    return (
        <CardContainer>
            <ContentPageHeader title={resources.pages.policyComponentDetails.header} />
            <ContentPageBody isMaxWidth={true}>

                {isLoaded && (
                    <PolicyComponentDetailsComponent
                        isDisabled={isDisabled}
                        policyComponentDetails={policyComponentDetails}
                        axnCompanies={referenceData.axnCompanies}
                        policyComponents={allowedPolicyComponents}
                        assertions={referenceData.axnAssertions}
                        apAssertions={referenceData.apAssertions}
                        serviceOfferings={referenceData.axnServiceOfferings}
                        axnAttributeTypes={referenceData.axnAttributeTypes}
                        axnAttributeTypeFields={referenceData.axnAttributeTypeFields}
                        acquiredAttributes={referenceData.axnAcquiredAttributes}
                        axnAvailableAssertionStatuses={referenceData.axnAvailableAssertionStatuses}
                        associatedPolicies={associatedPolicies}
                        associatedPoliciesTableState={associatedPoliciesTableState}
                        handleSave={handleSave}
                    />
                )}
                {(isLoading) && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </CardContainer>
    );
}